import {
  MagnifyingGlassIcon,
  ChevronUpDownIcon,
} from "@heroicons/react/24/outline";
import React, { useEffect, useState } from "react";
import { ArrowDownIcon } from "@heroicons/react/24/solid";
import {
  Card,
  CardHeader,
  Input,
  Typography,
  Button,
  CardBody,
  CardFooter,
  IconButton,
  Tooltip,
  Select,
  Option,
  Chip,
} from "@material-tailwind/react";
import { useAuthUserToken } from "../../hooks/useAuthUserToken";
import TableSkeleton from "../../components/Loaders/TableSkeleton";
import { Link } from "react-router-dom";
import { getAllVendorsByReviewer } from "../../helperFunctions/apiFunction";
import toast from "react-hot-toast";

const VendorReview = () => {
  const { refreshToken } = useAuthUserToken();
  const [templateData, setTemplateData] = useState([]);
  const [isLoading, setIsLoading] = useState(null);
  const [searchBy, setSearchBy] = useState("");
  const [sortBy, setSortBy] = useState("asc");
  const [status, setStatus] = useState("");
  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState(1);
  const [totalPage, setTotalPage] = useState(1);
  const [reloadData, setReloadData] = useState(false);

  const tableHead = [
    "Vendor Name",
    "Questionnaire Name",
    "Status",
    "View Response",
  ];

  useEffect(() => {
    setIsLoading(null);
    getAllVendorsByReviewer(
      refreshToken,
      searchBy,
      sortBy,
      limit,
      page,
      status
    ).then((data) => {
      if (data?.data?.code === 440)
        toast.error(data?.data?.message, {
          className: "text-[12px]",
        });
      setTotalPage(data?.data?.totalPages);
      setTemplateData(data?.data?.results);
      setIsLoading(false);
    });
  }, [reloadData, searchBy, sortBy, page, status]);

  const exportToExcel = () => {};

  return (
    <div className="app-main__inner !pl-20">
      <Card
        className="h-full w-full rounded-3xl shadow-xl"
        style={{ backgroundImage: `url('/assets/images/beams.jpg')` }}
      >
        <CardHeader
          floated={false}
          shadow={false}
          className=" bg-transparent rounded-none !overflow-visible"
        >
          <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between md:gap-8">
            <div>
              <Typography variant="h5" color="blue-gray">
                List of Vendor
              </Typography>
              <Typography color="gray" className="mt-1 font-normal">
                View all questionnaire and their compliance against compliance
                requirements
              </Typography>
            </div>
            <div className="flex flex-row md:flex-row items-center justify-end md:items-center gap-2 md:gap-8 mt-4 md:mt-0">
              <Button
                variant="outlined"
                size="sm"
                onClick={exportToExcel}
                loading={isLoading === null ? true : false}
              >
                <ArrowDownIcon strokeWidth={2} className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <div className="flex flex-col items-center justify-between gap-6 md:flex-row">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 w-full">
              <Select
                color="blue"
                label="Select Limit"
                className="!border-t-0 !border-[#b0bec5]"
                onChange={(event) => {
                  setLimit(event);
                  setIsLoading(null);
                }}
                value={limit}
              >
                <Option value={10}>10</Option>
                <Option value={20}>20</Option>
                <Option value={30}>30</Option>
                <Option value={40}>40</Option>
                <Option value={50}>50</Option>
              </Select>
              <Select
                color="purple"
                label="Sort By"
                className="!border-t-0 !border-[#b0bec5]"
                onChange={(event) => {
                  setSortBy(event);
                  setIsLoading(null);
                }}
                value={sortBy}
              >
                <Option value="asc">Ascending</Option>
                <Option value="desc">Descending</Option>
              </Select>
              <Select
                color="green"
                label="Status"
                className="!border-t-0 !border-[#b0bec5] -inset-y-5"
                onChange={(event) => {
                  setStatus(event);
                  setIsLoading(null);
                  setReloadData((cur) => !cur);
                }}
                value={status}
              >
                <Option value="">All Data</Option>
                <Option value="assigned">Assigned</Option>
                <Option value="submitted">Submitted</Option>
                <Option value="rejected">Rejected</Option>
                <Option value="reAssigned">Reassigned</Option>
                <Option value="reSubmitted">Resubmitted</Option>
              </Select>
            </div>
            <div className="w-full md:w-auto">
              <Input
                label="Search"
                value={searchBy}
                className="focus:!border-black focus:!border-2 focus:!border-t-0"
                onChange={(e) => setSearchBy(e.target.value)}
                icon={<MagnifyingGlassIcon className="h-5 w-5" />}
              />
            </div>
          </div>
        </CardHeader>
        <CardBody className="overflow-scroll px-3 p-0">
          <table className="mt-4 w-full min-w-max table-auto text-left">
            <thead>
              <tr>
                {tableHead.map((head, index) => (
                  <th
                    key={head}
                    className="cursor-pointer border-y border-blue-gray-100 bg-blue-gray-50/50 p-3 transition-colors hover:bg-blue-gray-50 w-32"
                  >
                    <Typography
                      variant="small"
                      color="blue-gray"
                      className="flex items-center justify-between gap-2 font-bold leading-none opacity-70"
                    >
                      {head}{" "}
                      {index !== tableHead.length - 1 && (
                        <ChevronUpDownIcon
                          strokeWidth={2}
                          className="h-4 w-4"
                        />
                      )}
                    </Typography>
                  </th>
                ))}
              </tr>
            </thead>
            {isLoading === null ? (
              <>
                <tbody>
                  <tr>
                    <td colSpan={tableHead.length}>
                      <TableSkeleton />
                    </td>
                  </tr>
                </tbody>
              </>
            ) : templateData && templateData.length > 0 ? (
              <>
                {templateData.map((item, index) => (
                  <React.Fragment key={item.id}>
                    {Array.isArray(item?.questionnaire) &&
                      item?.questionnaire.map((data, dataIndex) => {
                        const isLast =
                          dataIndex === item?.questionnaire.length - 1;
                        const classes = isLast
                          ? "p-4"
                          : "p-4 border-b border-blue-gray-50";
                        return (
                          <tr key={`${item.id}-${dataIndex}`}>
                            <td className={classes}>
                              <Typography
                                variant="small"
                                color="blue-gray"
                                className="font-normal"
                              >
                                {item.vendorLegalName}
                              </Typography>
                            </td>
                            <td className={classes}>
                              <Typography
                                variant="small"
                                color="blue-gray"
                                className="font-normal"
                              >
                                {data?.questionnaireId?.quesName}
                              </Typography>
                            </td>
                            <td className={classes}>
                              <div className="w-max">
                                <Chip
                                  variant="ghost"
                                  size="sm"
                                  value={data?.status}
                                  color={
                                    data?.status === "assigned"
                                      ? "yellow"
                                      : data?.status === "submitted"
                                      ? "gray"
                                      : data?.status === "reAssigned"
                                      ? "orange"
                                      : data?.status === "reSubmitted"
                                      ? "blue-gray"
                                      : data?.status === "rejected"
                                      ? "red"
                                      : data?.status === "accepted"
                                      ? "green"
                                      : "black"
                                  }
                                />
                              </div>
                            </td>
                            <td className={classes}>
                              <Tooltip
                                className="bg-blue-gray-500"
                                content="View Vendor Response"
                                placement="top"
                              >
                                <Link
                                  to={`/vendorReview/responseReview`}
                                  state={{
                                    vendorId: item?.id,
                                    questionnaireId: data?.questionnaireId?.id,
                                    vendorQuestionnaireId: data?._id,
                                  }}
                                >
                                  <Button
                                    variant="gradient"
                                    size="sm"
                                    className="no-underline"
                                    disabled={
                                      data?.status === "submitted" ||
                                      data?.status === "reSubmitted"
                                        ? false
                                        : true
                                    }
                                  >
                                    View Responses
                                  </Button>
                                </Link>
                              </Tooltip>
                            </td>
                          </tr>
                        );
                      })}
                  </React.Fragment>
                ))}
              </>
            ) : (
              <>
                <tr>
                  <td colSpan="8">
                    <div className="flex flex-col items-center w-full">
                      <img
                        quality={100}
                        src="/assets/images/9276430.jpg"
                        className="w-[350px] mix-blend-darken"
                        alt="no-data-found"
                      />
                      <p className="text-[20px] font-medium">No Data Found!</p>
                    </div>
                  </td>
                </tr>
              </>
            )}
          </table>
        </CardBody>
        {isLoading === null ? null : (
          <>
            <CardFooter className="flex items-center justify-between border-t border-blue-gray-50 p-4">
              <Button
                variant="outlined"
                size="sm"
                onClick={() => {
                  setPage(page - 1);
                  setIsLoading(null);
                }}
                disabled={totalPage < 1 ? false : true}
              >
                Previous
              </Button>
              <div className="flex items-center gap-2">
                {Array.from({ length: totalPage }, (_, index) => (
                  <IconButton
                    key={index + 1}
                    variant={index + 1 === page ? "outlined" : "text"}
                    size="sm"
                    onClick={() => {
                      setPage(index + 1);
                      setIsLoading(null);
                    }}
                    disabled={totalPage > 1 ? false : true}
                  >
                    {index + 1}
                  </IconButton>
                ))
                  .reduce((acc, button, index, array) => {
                    if (
                      index < 3 ||
                      index >= totalPage - 3 ||
                      (index >= Math.max(page - 1, 3) &&
                        index <= Math.min(page + 1, totalPage - 4))
                    ) {
                      acc.push(button);
                    } else if (acc[acc.length - 1] !== "ellipsis") {
                      acc.push("ellipsis");
                    }
                    return acc;
                  }, [])
                  .map((element, index, array) => {
                    return element === "ellipsis" ? (
                      <IconButton
                        key={`ellipsis-${index}`}
                        variant="text"
                        size="sm"
                        disabled
                      >
                        ...
                      </IconButton>
                    ) : index > 0 && array[index - 1] === "ellipsis" ? (
                      <React.Fragment key={`fragment-${index}`}>
                        {element}
                      </React.Fragment>
                    ) : (
                      element
                    );
                  })}
              </div>

              <Button
                variant="outlined"
                size="sm"
                onClick={() => {
                  setPage(page + 1);
                  setIsLoading(null);
                }}
                disabled={totalPage > 1 ? false : true}
              >
                Next
              </Button>
            </CardFooter>
          </>
        )}
      </Card>
    </div>
  );
};

export default VendorReview;
